// src/components/FeeManagement/FeeListComponent.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Typography, FormControl, InputLabel, Select, MenuItem, CircularProgress,
    Button, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Snackbar, Tab, Tabs, Alert, TablePagination, Grid,
    Tooltip, Card, CardContent, Avatar, Chip, Divider, Container, Stack
} from '@mui/material';
import { styled, alpha } from '@mui/system';
import axiosInstance from '../../api/axiosConfig'; // Adjust path if needed
import { getApiErrorMessage } from '../../api/errorUtils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import NoResultsImage from '../../assets/no-results.png'; // Adjust path if needed
import {
    buildReportHtml,
    escapeHtml,
    getSchoolReportProfile,
    openPrintPreview,
} from '../../utils/reportPrintUtils';

// Icons
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';

import PendingFeesComponent from './PendingFeesComponent'; // Assuming these exist
import FeesGraphComponent from './FeesGraphComponent';   // Assuming these exist
import { format } from 'date-fns'; // For date formatting
import { STORAGE_KEYS } from '../../constants';

// === Helper function for number to words ===
function numberToWords(num) {
    const a=['','one ','two ','three ','four ','five ','six ','seven ','eight ','nine ','ten ','eleven ','twelve ','thirteen ','fourteen ','fifteen ','sixteen ','seventeen ','eighteen ','nineteen '];const b=['','','twenty','thirty','forty','fifty','sixty','seventy','eighty','ninety'];const g=['','thousand','million','billion','trillion','quadrillion','quintillion'];function numToWordsChunk(n){let s='';const h=Math.floor(n/100);const r=n%100;if(h){s+=a[h]+'hundred '}if(r){s+=(h&&r?'and ':'')+(r<20?a[r]:b[Math.floor(r/10)]+(r%10?'-'+a[r%10]:''))}return s}if(num===null||num===undefined)return'Invalid Number';const nv=Number(num);if(isNaN(nv))return'Not a Number';if(nv===0)return'Zero';const ns=nv.toString();if(ns.includes('.')){const p=ns.split('.');const ip=parseInt(p[0],10);const ds=(p[1]||'').padEnd(2,'0').substring(0,2);const dp=parseInt(ds,10);let iw=(ip===0)?'':numberToWords(ip);let dw=(dp===0)?'':numberToWords(dp);let fw='';if(iw&&iw!=='Zero'){fw+=iw+' rupees '}if(dw&&dw!=='Zero'){fw+=(iw&&iw!=='Zero'?'and ':'')+dw+' paise '}if(!fw&&dw&&dw!=='Zero'){fw=dw+'paise '}else if(iw&&iw!=='Zero'&&!dw){fw=iw+' rupees'}else if(ip===0&&dp===0){return'Zero rupees'}fw=fw.trim().replace(/\s+/g,' ');return fw?fw.charAt(0).toUpperCase()+fw.slice(1):'Zero'}else{let w='';let cc=0;let n=Math.abs(parseInt(ns,10));if(n===0)return'Zero';while(n>0){const c=n%1000;if(c){w=numToWordsChunk(c)+(g[cc]?g[cc]+' ':'')+w}n=Math.floor(n/1000);cc++}w=w.trim().replace(/\s+/g,' ');return w.charAt(0).toUpperCase()+w.slice(1)}}

// --- Avatar Helper ---
function stringAvatar(name) {
    if (!name || name === 'N/A') return { children: 'NA' };
    const parts = name.trim().split(' ');
    const initials = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0][0];
    return { children: initials.toUpperCase() };
}

// --- Modern Styled Components ---
const PageBackground = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#121212' : '#f4f6f8',
    minHeight: '100%',
    padding: 0,
    [theme.breakpoints.down('sm')]: {
        padding: 0,
    },
}));

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 6,
    boxShadow: '0px 12px 34px rgba(15, 23, 42, 0.05)',
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
}));

const HeroStrip = styled(Box)(({ theme }) => ({
    borderRadius: 26,
    padding: theme.spacing(2.4),
    background: `linear-gradient(135deg, ${alpha('#FFF7ED', 0.95)} 0%, ${alpha('#FFFFFF', 1)} 55%, ${alpha('#FEF3C7', 0.9)} 100%)`,
    border: `1px solid ${alpha(theme.palette.warning.main, 0.18)}`,
    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.05)',
    marginBottom: theme.spacing(2.5),
}));

const MetricTile = styled(Box)(({ theme }) => ({
    height: '100%',
    borderRadius: 6,
    padding: theme.spacing(1.8),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
}));

const StyledTable = styled(Table)(({ theme }) => ({
    minWidth: 800,
    '& thead th': {
        backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : '#f8fafc',
        color: theme.palette.text.secondary,
        fontWeight: 600,
        fontSize: '0.85rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        padding: '16px',
        borderBottom: `2px solid ${theme.palette.divider}`,
    },
    '& tbody tr': {
        transition: 'background-color 0.2s ease',
    },
    '& tbody tr:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: '0.83rem',
    padding: '10px 12px',
    color: theme.palette.text.primary,
}));

const NoResultsContainer = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(8, 2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 6,
    border: `1px dashed ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
}));

// Modern Inputs
const CustomTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: theme.palette.background.paper,
        transition: 'box-shadow 0.2s ease',
        '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
    },
}));

const CustomFormControl = styled(FormControl)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: theme.palette.background.paper,
        transition: 'box-shadow 0.2s ease',
        '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
    },
}));

const formatNumber = (num) => {
    if (typeof num !== 'number' || isNaN(num)) return '0.00';
    return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// --- FeeListComponent ---
const FeeListComponent = () => {
    const [fees, setFees] = useState([]);
    const [filterType, setFilterType] = useState('academicYear');
    const[date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [academicYear] = useState(null);
    const [classFilter, setClassFilter] = useState('');
    const [sectionFilter, setSectionFilter] = useState('');
    const [classes, setClasses] = useState([]);
    const [allSections, setAllSections] = useState([]);
    const [sections, setSections] = useState([]);
    const [loadingClasses, setLoadingClasses] = useState(false);
    const [loadingSections, setLoadingSections] = useState(false);
    const [loading, setLoading] = useState(false);
    const[error, setError] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [page, setPage] = useState(0);
    const[rowsPerPage, setRowsPerPage] = useState(50);
    const [schoolDetails, setSchoolDetails] = useState({ name: 'School Name', address: '', contact: '', logoUrl: '' });

    useEffect(() => {
        const storedDetailsStr = localStorage.getItem('schoolDetails');
        let loadedDetails = { name: 'School Name', address: '', contact: '', logoUrl: '' }; 
        if (storedDetailsStr) {
            try {
                loadedDetails = JSON.parse(storedDetailsStr);
            } catch (e) { console.error("Failed to parse school details"); }
        } else {
            const email = localStorage.getItem(STORAGE_KEYS.SCHOOL_EMAIL);
            const phone = localStorage.getItem(STORAGE_KEYS.SCHOOL_PHONE);

            let contact = 'Contact Unavailable';
            if (email && phone) {
                contact = `${email} | ${phone}`;
            } else if (email) {
                contact = email;
            } else if (phone) {    
                contact = phone;
            }

            loadedDetails = {
                name: localStorage.getItem('schoolName') || 'School Name Unavailable',
                slogan: localStorage.getItem(STORAGE_KEYS.SCHOOL_SLOGAN) || null,
                address: localStorage.getItem(STORAGE_KEYS.SCHOOL_ADDRESS) || 'Address Unavailable',
                contact: contact,
                logoUrl: localStorage.getItem(STORAGE_KEYS.SCHOOL_LOGO) || null
            };
        }
        setSchoolDetails(loadedDetails);
    },[]);

    const fetchFees = useCallback(async () => {
        setLoading(true); setError(null); setFees([]); setPage(0);
        let url = ''; let params = {};
        try {
            switch (filterType) {
                case 'daily': url = '/api/fees/by-date'; params = { date }; break;
                case 'monthly': url = '/api/fees/by-month'; params = { month }; break;
                case 'academicYear': default: url = '/api/fees/by-academic-year'; params = { academicYear: academicYear || undefined }; break;
            }
            const response = await axiosInstance.get(url, { params });
            setFees(Array.isArray(response.data) ? response.data :[]);
        } catch (err) {
            console.error(`Error fetching fees for type ${filterType}:`, err);
            setError(getApiErrorMessage(err, 'Failed to fetch fee transactions.')); setFees([]);
        } finally { setLoading(false); }
    },[filterType, date, month, academicYear]);

    useEffect(() => { fetchFees(); },[fetchFees]);

    const schoolId = localStorage.getItem(STORAGE_KEYS.SCHOOL_ID);

    const filteredFees = useMemo(() => {
        let result = fees;
        if (classFilter) {
            result = result.filter(fee => String(fee.student?.classes?.classId || '') === String(classFilter));
        }
        if (sectionFilter) {
            result = result.filter(fee => String(fee.student?.section?.sectionId || '') === String(sectionFilter));
        }
        return result;
    }, [fees, classFilter, sectionFilter]);

    const handleFilterTypeChange = (event) => { setFilterType(event.target.value); setClassFilter(''); setSectionFilter(''); };
    const handleDateChange = (event) => setDate(event.target.value);
    const handleMonthChange = (event) => setMonth(event.target.value);
    const handleClassFilterChange = (event) => { setClassFilter(event.target.value); setSectionFilter(''); setPage(0); };
    const handleSectionFilterChange = (event) => { setSectionFilter(event.target.value); setPage(0); };
    const handleSnackbarClose = (event, reason) => { if (reason === 'clickaway') return; setSnackbarOpen(false); };
    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbarMessage(message); setSnackbarSeverity(severity); setSnackbarOpen(true);
    },[]);
    const handleChangePage = (event, newPage) => { setPage(newPage); };
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

    const getSafe = useCallback((obj, path, defaultValue = 'N/A') => {
        try { const value = path.split('.').reduce((acc, part) => acc && acc[part], obj); return value !== undefined && value !== null && value !== '' ? value : defaultValue; } catch (e) { return defaultValue; }
    },[]);

    const formatCurrency = useCallback((value, currency = 'INR', locale = 'en-IN') => {
        try { if (typeof value !== 'number' || isNaN(value)) return `${currency} -`; return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(value); } catch (e) { return `${currency} ${Number(value || 0).toFixed(2)}`; }
    },[]);

    useEffect(() => {
        if (!schoolId) return;
        setLoadingClasses(true);
        Promise.all([
            axiosInstance.get('/api/classes', { headers: { schoolId } }),
            axiosInstance.get('/api/sections', { headers: { schoolId } }),
        ])
            .then(([classesRes, sectionsRes]) => {
                setClasses(Array.isArray(classesRes.data) ? classesRes.data : []);
                setAllSections(Array.isArray(sectionsRes.data) ? sectionsRes.data : []);
            })
            .catch((err) => {
                setClasses([]);
                setAllSections([]);
                showSnackbar(getApiErrorMessage(err, 'Failed to load classes.'), 'warning');
            })
            .finally(() => setLoadingClasses(false));
    }, [schoolId, showSnackbar]);

    useEffect(() => {
        if (!schoolId || !classFilter) {
            setSections([]);
            return;
        }
        setLoadingSections(true);
        try {
            const selectedClass = classes.find((cls) => String(cls.classId) === String(classFilter));
            const assignedSectionIds = String(selectedClass?.assignedSections || '')
                .split(',')
                .map((value) => Number(String(value).trim()))
                .filter((value) => !Number.isNaN(value));
            const availableSections = allSections.filter((section) => assignedSectionIds.includes(Number(section.sectionId)));
            setSections(availableSections);
            if (!availableSections.some((section) => String(section.sectionId) === String(sectionFilter))) {
                setSectionFilter('');
            }
        } finally {
            setLoadingSections(false);
        }
    }, [schoolId, classFilter, sectionFilter, classes, allSections]);

    const totalCollected = useMemo(
        () => filteredFees.reduce((sum, fee) => sum + (Number(fee.amount) || 0), 0),
        [filteredFees]
    );

    const uniqueStudents = useMemo(
        () => new Set(filteredFees.map((fee) => fee.student?.studentId).filter(Boolean)).size,
        [filteredFees]
    );

    const activeTimelineLabel = useMemo(() => {
        if (filterType === 'daily') {
            return date ? format(new Date(date), 'dd MMM yyyy') : 'Today';
        }
        if (filterType === 'monthly') {
            return month ? format(new Date(`${month}-01`), 'MMM yyyy') : 'This Month';
        }
        return academicYear || 'Current Academic Year';
    }, [academicYear, date, filterType, month]);

    // Print Receipt Logic
    const handlePrintReceipt = useCallback((fee) => {
        if (!fee || !fee.student) { showSnackbar('Cannot generate receipt: Missing data.', 'error'); return; }

        const studentName = `${getSafe(fee.student, 'firstName')} ${getSafe(fee.student, 'lastName')}`;
        const admissionNo = getSafe(fee.student, 'admissionNumber');
        const classSectionString = `${getSafe(fee.student, 'classes.className', '')}${getSafe(fee.student, 'section.sectionName', '') ? ` / ${getSafe(fee.student, 'section.sectionName')}` : ''}` || 'N/A';
        const schoolName = getSafe(schoolDetails, 'name', 'School Name');
        const schoolAddress = getSafe(schoolDetails, 'address', 'School Address');
        const schoolContact = getSafe(schoolDetails, 'contact', 'School Contact');
        const schoolLogoUrl = schoolDetails.logoUrl || '';
        const receiptNo = `R${String(getSafe(fee, 'feeId', '000')).padStart(5, '0')}`;
        const paymentDate = fee.paymentDate ? format(new Date(fee.paymentDate.split('T')[0]), 'dd MMM yyyy') : 'N/A';
        const academicYear = getSafe(fee, 'academicYear', getSafe(fee.student, 'academicYear', 'N/A'));
        const amountPaid = parseFloat(getSafe(fee, 'amount', 0)) || 0;
        const amountInWords = numberToWords(amountPaid);
        const paymentMode = getSafe(fee, 'paymentMode');
        const referenceNumber = getSafe(fee, 'referenceNumber', '-');
        const comments = getSafe(fee, 'comments', '-');
        try {
            const school = {
                ...getSchoolReportProfile(),
                schoolName: schoolName || getSchoolReportProfile().schoolName,
                address: schoolAddress || getSchoolReportProfile().address,
                contact: schoolContact || getSchoolReportProfile().contact,
                logoUrl: schoolLogoUrl || getSchoolReportProfile().logoUrl,
            };
            const receiptHtml = buildReportHtml({
                title: 'Fee Payment Receipt',
                subtitle: `${studentName} • ${receiptNo}`,
                school,
                orientation: 'landscape',
                body: `
                    <div class="meta-grid">
                        <div class="meta-card"><div class="label">Receipt No</div><div class="value">${escapeHtml(receiptNo)}</div></div>
                        <div class="meta-card"><div class="label">Payment Date</div><div class="value">${escapeHtml(paymentDate)}</div></div>
                        <div class="meta-card"><div class="label">Academic Year</div><div class="value">${escapeHtml(academicYear)}</div></div>
                        <div class="meta-card"><div class="label">Mode</div><div class="value">${escapeHtml(paymentMode || 'N/A')}</div></div>
                    </div>
                    <table>
                        <tbody>
                            <tr><th>Student Name</th><td>${escapeHtml(studentName)}</td><th>Admission No</th><td>${escapeHtml(admissionNo)}</td></tr>
                            <tr><th>Class / Section</th><td>${escapeHtml(classSectionString)}</td><th>Reference</th><td>${escapeHtml(referenceNumber)}</td></tr>
                            <tr><th>Amount Paid</th><td>${escapeHtml(formatCurrency(amountPaid))}</td><th>In Words</th><td>${escapeHtml(`${amountInWords} only`)}</td></tr>
                        </tbody>
                    </table>
                    ${comments !== '-' ? `<div class="note"><strong>Comments:</strong> ${escapeHtml(comments)}</div>` : ''}
                    <div class="footer">
                        <div>Generated from Skhool fee records.</div>
                        <div>Received By ____________________</div>
                    </div>
                `,
            });
            openPrintPreview({ title: `Fee Receipt - ${studentName}`, html: receiptHtml });
        } catch (error) {
             console.error("Print Error:", error);
             showSnackbar(error.message || 'Could not open print window.', 'error');
        }
    },[schoolDetails, getSafe, formatCurrency, showSnackbar]);

    const handleExportPDF = useCallback(() => {
        if (filteredFees.length === 0) { showSnackbar('No data to export.', 'warning'); return; }
        const doc = new jsPDF();
        const tableColumn =["Payment Date", "Student Name", "Class", "Section", "Amount (₹)", "Mode", "Status", "Reference No"];
        const tableRows =[];
        filteredFees.forEach(fee => {
            const studentName = fee.student ? `${fee.student.firstName || ''} ${fee.student.lastName || ''}`.trim() : 'N/A';
            const feeData =[ fee.paymentDate ? format(new Date(fee.paymentDate.split('T')[0]), 'dd/MM/yyyy') : 'N/A', studentName, fee.student?.classes?.className || 'N/A', fee.student?.section?.sectionName || 'N/A', formatNumber(fee.amount), fee.paymentMode || 'N/A', fee.status || 'N/A', fee.referenceNumber || '', ];
            tableRows.push(feeData);
        });
        const dateStr = format(new Date(), 'dd MMM yyyy');
        const filterDesc = filterType === 'daily' ? `Date: ${format(new Date(date), 'dd MMM yyyy')}` : filterType === 'monthly' ? `Month: ${format(new Date(month + '-01'), 'MMM yyyy')}` : `Academic Year: ${academicYear || 'All'}`; 
        doc.setFontSize(16); doc.text(`Fee Transactions Report`, 14, 15);
        doc.setFontSize(10); doc.text(`Filter: ${filterDesc}`, 14, 20); doc.text(`Generated on: ${dateStr}`, doc.internal.pageSize.getWidth() - 14, 20, { align: 'right' });
        doc.autoTable({ head: [tableColumn], body: tableRows, startY: 25, headStyles: { fillColor:[22, 160, 133], textColor: 255 }, styles: { fontSize: 9, cellPadding: 2 }, columnStyles: { 4: { halign: 'right' } }, margin: { top: 10 } });
        const pageCount = doc.internal.getNumberOfPages(); doc.setFontSize(8);
        for (let i = 1; i <= pageCount; i++) { doc.setPage(i); doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' }); }
        doc.save(`fees_report_${filterType}_${format(new Date(), 'yyyyMMdd')}.pdf`);
        showSnackbar('Report exported successfully!', 'success');
    },[filteredFees, filterType, date, month, academicYear, showSnackbar]);

    const paginatedFees = filteredFees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box>
            <HeroStrip>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                        <Typography variant="overline" sx={{ color: 'warning.dark', fontWeight: 800, letterSpacing: '0.12em' }}>
                            Collections Desk
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 0.7, fontWeight: 800, color: 'text.primary' }}>
                            See collections, receipts, and gaps without digging
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.1, lineHeight: 1.7 }}>
                            Filter by day, month, or academic year, then narrow by class and section to understand exactly where collections are healthy and where follow-up is needed.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={4}>
                                <MetricTile>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                                                Collected
                                            </Typography>
                                            <Typography variant="h6" sx={{ mt: 0.8, fontWeight: 800 }}>
                                                {formatCurrency(totalCollected)}
                                            </Typography>
                                        </Box>
                                        <AccountBalanceWalletIcon sx={{ color: 'success.main' }} />
                                    </Stack>
                                </MetricTile>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MetricTile>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                                                Students
                                            </Typography>
                                            <Typography variant="h6" sx={{ mt: 0.8, fontWeight: 800 }}>
                                                {uniqueStudents || 0}
                                            </Typography>
                                        </Box>
                                        <Diversity3Icon sx={{ color: 'primary.main' }} />
                                    </Stack>
                                </MetricTile>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MetricTile>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                                                Timeline
                                            </Typography>
                                            <Typography variant="h6" sx={{ mt: 0.8, fontWeight: 800 }}>
                                                {activeTimelineLabel}
                                            </Typography>
                                        </Box>
                                        <EventRepeatIcon sx={{ color: 'warning.main' }} />
                                    </Stack>
                                </MetricTile>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </HeroStrip>

            <StyledCard elevation={0}>
                <CardContent sx={{ pb: '16px !important', p: { xs: 2, sm: 3 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                        <Typography variant="h6" fontWeight="600" color="text.primary">
                            Filter and Review
                        </Typography>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleExportPDF} 
                            startIcon={<PictureAsPdfIcon />} 
                            disabled={loading || filteredFees.length === 0} 
                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
                        > 
                            Export to PDF 
                        </Button>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={2.5} alignItems="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <CustomFormControl fullWidth variant="outlined" size="small" disabled={loading}>
                                <InputLabel id="filter-type-label">Filter Timeline</InputLabel>
                                <Select labelId="filter-type-label" value={filterType} label="Filter Timeline" onChange={handleFilterTypeChange}>
                                    <MenuItem value="daily">Daily</MenuItem> 
                                    <MenuItem value="monthly">Monthly</MenuItem> 
                                    <MenuItem value="academicYear">Academic Year</MenuItem>
                                </Select>
                            </CustomFormControl>
                        </Grid>
                        {filterType === 'daily' && ( 
                            <Grid item xs={12} sm={6} md={3}>
                                <CustomTextField type="date" label="Select Date" value={date} onChange={handleDateChange} InputLabelProps={{ shrink: true }} fullWidth size="small" variant="outlined" disabled={loading} />
                            </Grid> 
                        )}
                        {filterType === 'monthly' && ( 
                            <Grid item xs={12} sm={6} md={3}>
                                <CustomTextField type="month" label="Select Month" value={month} onChange={handleMonthChange} InputLabelProps={{ shrink: true }} fullWidth size="small" variant="outlined" disabled={loading} />
                            </Grid> 
                        )}
                        <Grid item xs={12} sm={6} md={3}> 
                            <CustomFormControl fullWidth size="small" disabled={loading || loadingClasses}>
                                <InputLabel id="fee-filter-class-label">Filter Class</InputLabel>
                                <Select
                                    labelId="fee-filter-class-label"
                                    value={classFilter}
                                    onChange={handleClassFilterChange}
                                    label="Filter Class"
                                >
                                    <MenuItem value="">All Classes</MenuItem>
                                    {classes.map((cls) => (
                                        <MenuItem key={cls.classId} value={String(cls.classId)}>
                                            {cls.className || 'Unnamed Class'}
                                        </MenuItem>
                                    ))}
                                    {!classes.length && !loadingClasses && (
                                        <MenuItem value="" disabled>
                                            No classes available
                                        </MenuItem>
                                    )}
                                </Select>
                            </CustomFormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}> 
                            <CustomFormControl fullWidth size="small" disabled={loading || loadingSections || !classFilter}>
                                <InputLabel id="fee-filter-section-label">Filter Section</InputLabel>
                                <Select
                                    labelId="fee-filter-section-label"
                                    value={sectionFilter}
                                    onChange={handleSectionFilterChange}
                                    label="Filter Section"
                                >
                                    <MenuItem value="">All Sections</MenuItem>
                                    {sections.map((section) => (
                                        <MenuItem key={section.sectionId} value={String(section.sectionId)}>
                                            {section.sectionName || 'Unnamed Section'}
                                        </MenuItem>
                                    ))}
                                    {!classFilter && (
                                        <MenuItem value="" disabled>
                                            Select class first
                                        </MenuItem>
                                    )}
                                    {classFilter && !sections.length && !loadingSections && (
                                        <MenuItem value="" disabled>
                                            No assigned sections
                                        </MenuItem>
                                    )}
                                </Select>
                            </CustomFormControl>
                        </Grid>
                    </Grid>
                </CardContent>
            </StyledCard>

            {loading && ( 
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', flexDirection: 'column' }}> 
                    <CircularProgress size={40} thickness={4} /> 
                    <Typography sx={{ mt: 2, fontWeight: 500 }} color="text.secondary">Fetching transactions...</Typography> 
                </Box> 
            )}
            
            {error && !loading && ( <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>{error}</Alert> )}

            {!loading && !error && (
                <>
                    {filteredFees.length === 0 ? (
                         <NoResultsContainer>
                             <img src={NoResultsImage} alt="No Results" style={{ width: '150px', opacity: 0.8, marginBottom: '16px' }} />
                             <Typography variant="h6" color="text.primary" fontWeight="600"> No fee transactions found </Typography>
                             <Typography color="text.secondary" variant="body2" mt={1}> Try adjusting the timeline or search filters above. </Typography>
                         </NoResultsContainer>
                    ) : (
                        <StyledCard elevation={0} sx={{ borderLeft: 'none', borderRight: 'none' }}>
                             <Box
                                sx={{
                                    px: { xs: 2, sm: 3 },
                                    py: 1.8,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: alpha('#0F172A', 0.015),
                                }}
                             >
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between">
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        <Chip size="small" variant="outlined" label={`Transactions: ${filteredFees.length}`} />
                                        <Chip size="small" variant="outlined" label={`Collected: ${formatCurrency(totalCollected)}`} />
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        Timeline: {activeTimelineLabel}
                                    </Typography>
                                </Stack>
                             </Box>
                             <TableContainer sx={{ maxHeight: 600 }}>
                                <StyledTable stickyHeader aria-label="modern fee transactions table">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Date</StyledTableCell>
                                            <StyledTableCell>Student</StyledTableCell>
                                            <StyledTableCell>Class / Section</StyledTableCell>
                                            <StyledTableCell align="right">Amount</StyledTableCell>
                                            <StyledTableCell align="center">Receipt</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedFees.map((fee) => {
                                            const studentName = fee.student ? `${fee.student.firstName || ''} ${fee.student.lastName || ''}`.trim() : 'N/A';
                                            const className = fee.student?.classes?.className || 'N/A';
                                            const sectionName = fee.student?.section?.sectionName || 'N/A';
                                            return (
                                                <TableRow hover key={fee.feeId}>
                                                    <StyledTableCell>
                                                        <Typography variant="body2" fontWeight="500">
                                                            {fee.paymentDate ? format(new Date(fee.paymentDate.split('T')[0]), 'dd MMM yyyy') : 'N/A'}
                                                        </Typography>
                                                    </StyledTableCell>
                                                    <StyledTableCell>
                                                        <Box display="flex" alignItems="center" gap={1.5}>
                                                            <Avatar 
                                                                sx={{ width: 30, height: 30, fontSize: '0.78rem', bgcolor: 'primary.light', color: 'primary.contrastText', fontWeight: 600 }} 
                                                                {...stringAvatar(studentName)} 
                                                            />
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography variant="body2" fontWeight="700" noWrap>{studentName}</Typography>
                                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                                    {fee.student?.admissionNumber || 'Admission N/A'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </StyledTableCell>
                                                    <StyledTableCell>
                                                        <Typography variant="body2" color="text.secondary" noWrap>
                                                            {className} &bull; {sectionName}
                                                        </Typography>
                                                    </StyledTableCell>
                                                    <StyledTableCell align="right">
                                                        <Typography variant="body2" fontWeight="700" color="text.primary">
                                                            ₹ {formatNumber(fee.amount)}
                                                        </Typography>
                                                    </StyledTableCell>
                                                    <StyledTableCell align="center">
                                                        <Tooltip title="Print A5 Receipt" arrow>
                                                            <Button
                                                                variant="outlined" 
                                                                color="primary" 
                                                                size="small"
                                                                onClick={() => handlePrintReceipt(fee)}
                                                                startIcon={<ReceiptLongIcon />}
                                                                disabled={!fee.student}
                                                                sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 600 }}
                                                            > 
                                                                Receipt 
                                                            </Button>
                                                        </Tooltip>
                                                    </StyledTableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </StyledTable>
                             </TableContainer>
                             <TablePagination
                                component="div" 
                                count={filteredFees.length} 
                                page={page} 
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage} 
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[50, 100, 200]}
                                sx={(theme) => ({
                                    borderTop: 'none',
                                    mt: 1.5,
                                    px: 0,
                                    py: 0,
                                    borderRadius: 0,
                                    backgroundColor: 'transparent',
                                    '& .MuiTablePagination-toolbar': {
                                        padding: 0,
                                        minHeight: 36,
                                        gap: theme.spacing(1),
                                        flexWrap: 'wrap',
                                        justifyContent: 'flex-end',
                                    },
                                    '& .MuiTablePagination-spacer': {
                                        display: 'none',
                                    },
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        color: theme.palette.text.secondary,
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                    },
                                    '& .MuiTablePagination-select': {
                                        fontWeight: 700,
                                        fontSize: '0.85rem',
                                    },
                                    '& .MuiTablePagination-actions button': {
                                        borderRadius: 10,
                                        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                                        marginLeft: theme.spacing(0.5),
                                    },
                                })}
                            />
                        </StyledCard>
                    )}
                </>
            )}
             <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                 <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} variant="filled" sx={{ width: '100%', borderRadius: '8px' }}> {snackbarMessage} </Alert>
            </Snackbar>
        </Box>
    );
};

// --- TabPanel ---
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return ( 
      <div role="tabpanel" hidden={value !== index} id={`fee-tabpanel-${index}`} aria-labelledby={`fee-tab-${index}`} {...other}> 
          {value === index && ( <Box sx={{ pt: 0 }}> {children} </Box> )} 
      </div> 
  );
}

// --- FeeManagementTabs ---
const FeeManagementTabs = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (event, newValue) => setTabIndex(newValue);

  return (
    <PageBackground sx={{ backgroundColor: 'transparent' }}>
        <Container maxWidth="lg" disableGutters>
            <Box sx={{ width: '100%' }}>
                <Box
                    sx={(theme) => ({
                        mb: 2,
                        p: 0,
                        borderRadius: 6,
                    })}
                >
                    <Tabs 
                        value={tabIndex} 
                        onChange={handleTabChange} 
                        variant="scrollable" 
                        scrollButtons={false}
                        aria-label="Fee management sections"
                        sx={(theme) => ({
                            minHeight: 44,
                            padding: 0,
                            '& .MuiTabs-indicator': { display: 'none' },
                            '& .MuiTabs-scroller': {
                                padding: 0,
                            },
                            '& .MuiTabs-flexContainer': {
                                gap: 0.6,
                                padding: 0,
                                margin: 0,
                            },
                            '& .MuiTab-root': {
                                minHeight: 38,
                                minWidth: 0,
                                padding: '6px 12px',
                                textTransform: 'none',
                                fontSize: '0.92rem',
                                fontWeight: 700,
                                borderRadius: 10,
                                color: theme.palette.text.secondary,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                },
                                '&.Mui-selected': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: 'transparent',
                                    boxShadow: 'none',
                                },
                            },
                            '& .MuiTabs-scrollButtons': {
                                display: 'none',
                            },
                        })}
                    >
                        <Tab disableRipple label="Transactions" id="fee-tab-0" aria-controls="fee-tabpanel-0" />
                        <Tab disableRipple label="Pending Fees" id="fee-tab-1" aria-controls="fee-tabpanel-1" />
                        <Tab disableRipple label="Analytics" id="fee-tab-2" aria-controls="fee-tabpanel-2" />
                    </Tabs>
                </Box>
                <TabPanel value={tabIndex} index={0}> <FeeListComponent /> </TabPanel>
                <TabPanel value={tabIndex} index={1}> <PendingFeesComponent /> </TabPanel>
                <TabPanel value={tabIndex} index={2}> <FeesGraphComponent /> </TabPanel>
            </Box>
        </Container>
    </PageBackground>
  );
};

export default FeeManagementTabs;
